import {
  CrudFilters,
  CrudSorting,
  DataProvider,
  Pagination
} from '@pankod/refine-core';

import { api } from 'services/api';

interface ListResponse {
  data: Record<string, any>[];
  paging?: { total: number };
}

const unsupported = (operation: string) => () =>
  Promise.reject(new Error(`${operation} is not supported by this resource`));

/**
 * A DataProvider whose every operation rejects. Resource providers spread
 * their own `getOne` / `create` / `update` / ... over it.
 */
export const createRestProvider = (): DataProvider => ({
  getList: unsupported('getList'),
  getOne: unsupported('getOne'),
  create: unsupported('create'),
  update: unsupported('update'),
  deleteOne: unsupported('deleteOne'),
  getApiUrl: () => ''
});

/** `name_xhr` -> `nameXhr`; digits stay attached (`address4`). */
const toCamel = (field: string) =>
  field.replace(/_([a-z0-9])/g, (_, char: string) => char.toUpperCase());

/** `nameXhr` -> `name_xhr`; digits stay attached (`address4`). */
const toSnake = (field: string) =>
  field.replace(/[A-Z]/g, char => `_${char.toLowerCase()}`);

const lastSegment = (field: string) => field.split('.').pop() as string;

const mapKeys = (row: Record<string, any>) =>
  Object.fromEntries(Object.entries(row).map(([k, v]) => [toSnake(k), v]));

/** The API's query field for a table column / filter field. */
const toQueryField = (field: string) => {
  const name = lastSegment(field);
  return name === 'product_type' ? 'type' : toCamel(name);
};

/**
 * Turns refine filters into query params. `contains` values arrive wrapped in
 * `%` (see buildFilterItem); the API adds the wildcards itself.
 */
export const filtersToParams = (filters: CrudFilters = []) => {
  const params: Record<string, string | number> = {};
  filters.forEach(filter => {
    if (!('field' in filter) || filter.operator === 'null') return;
    const { field, operator, value } = filter;
    if (value === null || value === undefined || value === '') return;
    params[toQueryField(field)] =
      operator === 'contains' ? String(value).replace(/^%|%$/g, '') : value;
  });
  return params;
};

export const sortToParam = (sort: CrudSorting = []) => {
  if (!sort.length) return undefined;
  return JSON.stringify(
    Object.fromEntries(
      sort.map(({ field, order }) => [toCamel(lastSegment(field)), order])
    )
  );
};

const paginationParams = (pagination?: Pagination) =>
  pagination ? { page: pagination.current, pageSize: pagination.pageSize } : {};

const listRequest = async (
  url: string,
  {
    filters,
    sort,
    pagination
  }: { filters?: CrudFilters; sort?: CrudSorting; pagination?: Pagination },
  omitParams: string[] = []
) => {
  const params: Record<string, any> = {
    ...filtersToParams(filters),
    ...paginationParams(pagination),
    sortBy: sortToParam(sort)
  };
  omitParams.forEach(name => delete params[name]);
  return api.get<ListResponse>(url, { params });
};

const toProductRow = (row: Record<string, any>) => ({
  ...mapKeys(row),
  // ProductList reads the guide through the tour / transportation relation
  tour: { id: row.id, guide: row.guide },
  transportation: { id: row.id, guide: row.guide }
});

const toSupplierProductRow = (row: Record<string, any>) => ({
  ...mapKeys(row),
  product_mappings: (row.providers as string[]).map(name => ({
    provider: { name }
  }))
});

/**
 * Backs the admin list screens (and the product recommendation switch) with
 * the `/admin/*` REST API.
 */
export const defaultDataProvider: DataProvider = {
  ...createRestProvider(),

  getList: async ({ resource, filters, sort, pagination }) => {
    const query = { filters, sort, pagination };
    let response: ListResponse;
    let toRow: (row: Record<string, any>) => Record<string, any> = mapKeys;

    switch (resource) {
      case 'suppliers':
        response = await listRequest('/admin/suppliers', query);
        break;
      case 'products': {
        const { supplierId } = filtersToParams(filters);
        if (supplierId) {
          response = await listRequest(
            `/admin/suppliers/${supplierId}/products`,
            query,
            ['supplierId']
          );
          toRow = toSupplierProductRow;
        } else {
          response = await listRequest('/admin/products', query);
          toRow = toProductRow;
        }
        break;
      }
      case 'locations':
        response = await listRequest('/admin/locations', query);
        break;
      case 'organizations':
        response = await listRequest('/admin/organizations', query);
        break;
      case 'sync_statuses':
        response = await listRequest('/admin/sync-statuses', query);
        break;
      default:
        throw new Error(`getList is not supported for ${resource}`);
    }

    return {
      data: response.data.map(toRow) as any,
      total: response.paging?.total ?? response.data.length
    };
  },

  update: async ({ resource, id, variables }) => {
    const values = (variables || {}) as Record<string, any>;
    if (resource !== 'products' || typeof values.is_recommended !== 'boolean') {
      throw new Error(`update is not supported for ${resource}`);
    }

    await api.patch(`/admin/products/${id}/recommendation`, {
      isRecommended: values.is_recommended
    });
    return { data: { id, ...values } as any };
  }
};
